import {
  ArrowLeft,
  BookOpen,
  Calendar,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  Loader2,
  Search
} from 'lucide-react';
import React, { useEffect, useState } from 'react';

import { useTranslation } from '@/components/i18n/Translator';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

interface DocType {
  slug: string;
  doktyp: string;
  name: string;
  description: string;
}

interface DokumentItem {
  id: string;
  dok_id: string;
  rm: string;
  beteckning: string;
  doktyp: string;
  titel: string;
  subtitel: string;
  datum: string;
  organ: string;
  dokument_url_html: string;
}

interface DokumentResponse {
  hits: number;
  page: number;
  total_pages: number;
  dokument: DokumentItem[];
}

const getApiBaseUrl = () => {
  if (typeof window !== 'undefined') {
    if (
      window.location.hostname === 'localhost' ||
      window.location.hostname === '127.0.0.1'
    ) {
      return 'http://localhost:8000';
    }
    return window.location.origin;
  }
  return 'http://localhost:8000';
};

// ─── Overview grid ────────────────────────────────────────────────────────────

const OverviewGrid = ({
  types,
  onSelect
}: {
  types: DocType[];
  onSelect: (t: DocType) => void;
}) => {
  const { t } = useTranslation();
  const [globalSearch, setGlobalSearch] = useState('');
  const [globalRm, setGlobalRm] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchResults, setSearchResults] = useState<DokumentResponse | null>(
    null
  );
  const [searchError, setSearchError] = useState<string | null>(null);

  const handleGlobalSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (globalSearch.length < 2) return;
    setSearching(true);
    setSearchError(null);
    const base = getApiBaseUrl();
    const params = new URLSearchParams({
      sok: globalSearch,
      page: '1',
      sz: '20'
    });
    if (globalRm) params.set('rm', globalRm);
    try {
      const res = await fetch(`${base}/chat/api/dokument/search?${params}`, {
        credentials: 'include'
      });
      if (!res.ok) throw new Error('Search failed');
      setSearchResults(await res.json());
    } catch (err) {
      setSearchError(err instanceof Error ? err.message : 'Search failed');
    } finally {
      setSearching(false);
    }
  };

  return (
    <div className="flex gap-6 h-full">
      {/* Left column — type list */}
      <div className="w-64 shrink-0">
        <Card className="h-full">
          <CardHeader className="pb-2 px-4 pt-4">
            <CardTitle className="text-sm font-semibold text-gray-700">
              {t('dokument.typeList.label')}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0 overflow-y-auto max-h-[70vh]">
            <ul className="divide-y divide-gray-100">
              {types.map((docType) => (
                <li key={docType.slug}>
                  <button
                    onClick={() => onSelect(docType)}
                    className="w-full text-left px-4 py-2.5 flex items-center justify-between hover:bg-gray-50 transition-colors group"
                  >
                    <span className="text-sm text-gray-900 group-hover:text-blue-700 truncate">
                      {docType.name}
                    </span>
                    <Badge
                      variant="outline"
                      className="ml-2 shrink-0 font-mono text-xs"
                    >
                      {docType.doktyp}
                    </Badge>
                  </button>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      </div>

      {/* Right column — search */}
      <div className="flex-1 min-w-0 space-y-4">
        {/* Search form */}
        <Card>
          <CardContent className="pt-5 pb-5">
            <form onSubmit={handleGlobalSearch} className="space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="md:col-span-2 space-y-1.5">
                  <Label htmlFor="dok-global-search">
                    {t('dokument.form.globalSearch')}
                  </Label>
                  <Input
                    id="dok-global-search"
                    value={globalSearch}
                    onChange={(e) => setGlobalSearch(e.target.value)}
                    placeholder={t('dokument.form.globalSearchPlaceholder')}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="dok-global-rm">{t('dokument.form.riksmote')}</Label>
                  <Input
                    id="dok-global-rm"
                    value={globalRm}
                    onChange={(e) => setGlobalRm(e.target.value)}
                    placeholder={t('dokument.form.riksmotePlaceholder')}
                  />
                </div>
              </div>
              <Button
                type="submit"
                disabled={searching || globalSearch.length < 2}
              >
                <Search className="h-4 w-4 mr-2" />
                {searching ? t('dokument.actions.searching') : t('dokument.actions.search')}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Results */}
        {searching && (
          <Card>
            <CardContent className="flex items-center justify-center py-8 gap-2">
              <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
              <span>{t('dokument.actions.searching')}</span>
            </CardContent>
          </Card>
        )}

        {searchError && (
          <Card className="border-red-200 bg-red-50">
            <CardContent className="pt-5 text-red-700 text-sm">
              {searchError}
            </CardContent>
          </Card>
        )}

        {searchResults && !searching && (
          <div className="space-y-3">
            <p className="text-sm text-gray-600 font-medium">
              <strong>{searchResults.hits.toLocaleString()}</strong>{' '}
              — {t('eurlex.results.pageLabel', { page: searchResults.page })} / {searchResults.total_pages}
            </p>
            {searchResults.dokument.length === 0 ? (
              <Card>
                <CardContent className="py-6 text-center text-gray-500 text-sm">
                  {t('dokument.results.noResults')}
                </CardContent>
              </Card>
            ) : (
              searchResults.dokument.map((doc) => (
                <DokumentCard key={doc.id || doc.dok_id} doc={doc} />
              ))
            )}
          </div>
        )}

        {!searchResults && !searching && (
          <Card className="border-dashed">
            <CardContent className="py-16 text-center text-gray-400 text-sm">
              {t('dokument.results.placeholder')}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

// ─── Single document card ──────────────────────────────────────────────────────

const DokumentCard = ({ doc }: { doc: DokumentItem }) => {
  const { t } = useTranslation();
  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardContent className="pt-5">
        <div className="flex flex-wrap gap-2 mb-2">
          {doc.beteckning && (
            <Badge variant="outline" className="font-mono text-xs">
              {doc.beteckning}
            </Badge>
          )}
          {doc.doktyp && (
            <Badge variant="secondary" className="text-xs">
              {doc.doktyp.toUpperCase()}
            </Badge>
          )}
          {doc.rm && (
            <Badge className="text-xs bg-gray-100 text-gray-700 border-gray-200">
              {doc.rm}
            </Badge>
          )}
        </div>

        <p className="font-medium text-gray-900 leading-snug mb-1">
          {doc.titel || '(Ingen titel)'}
        </p>
        {doc.subtitel && (
          <p className="text-sm text-gray-500 mb-2">{doc.subtitel}</p>
        )}

        <div className="text-xs text-gray-500 flex flex-wrap gap-4 mb-3">
          {doc.datum && (
            <span>
              <Calendar className="h-3 w-3 inline mr-1" />
              {doc.datum}
            </span>
          )}
          {doc.organ && <span>{t('dokument.organ', { organ: doc.organ })}</span>}
        </div>

        {doc.dokument_url_html && (
          <Button variant="outline" size="sm" asChild>
            <a href={doc.dokument_url_html} target="_blank" rel="noopener noreferrer">
              <ExternalLink className="h-3 w-3 mr-1" />
              {t('dokument.actions.openDocument')}
            </a>
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

// ─── Type detail view ──────────────────────────────────────────────────────────

const TypeView = ({
  docType,
  onBack
}: {
  docType: DocType;
  onBack: () => void;
}) => {
  const [sok, setSok] = useState('');
  const [rm, setRm] = useState('');
  const [page, setPage] = useState(1);
  const [data, setData] = useState<DokumentResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { t } = useTranslation();

  const fetchDocs = async (p: number = 1) => {
    setLoading(true);
    setError(null);
    setPage(p);
    const base = getApiBaseUrl();
    const params = new URLSearchParams({ page: String(p), sz: '20' });
    if (sok) params.set('sok', sok);
    if (rm) params.set('rm', rm);
    try {
      const res = await fetch(
        `${base}/chat/api/dokument/${docType.slug}?${params}`,
        { credentials: 'include' }
      );
      if (!res.ok) throw new Error('Hämtning misslyckades');
      setData(await res.json());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Fel vid hämtning');
    } finally {
      setLoading(false);
    }
  };

  // Load on mount
  useEffect(() => {
    fetchDocs(1);
  }, [docType.slug]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    fetchDocs(1);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={onBack}>
          <ArrowLeft className="h-4 w-4 mr-1" />
          {t('dokument.actions.back')}
        </Button>
        <div>
          <h3 className="text-xl font-bold text-gray-900">{docType.name}</h3>
          <p className="text-sm text-gray-500">{docType.description}</p>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <form
            onSubmit={handleSearch}
            className="grid grid-cols-1 md:grid-cols-3 gap-4"
          >
            <div className="md:col-span-2 space-y-2">
              <Label htmlFor="dok-type-sok">{t('dokument.form.typeSearch')}</Label>
              <Input
                id="dok-type-sok"
                value={sok}
                onChange={(e) => setSok(e.target.value)}
                placeholder={t('dokument.form.typeSearchPlaceholder')}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="dok-type-rm">{t('dokument.form.riksmote')}</Label>
              <Input
                id="dok-type-rm"
                value={rm}
                onChange={(e) => setRm(e.target.value)}
                placeholder={t('dokument.form.riksmotePlaceholder')}
              />
            </div>
            <div className="md:col-span-3">
              <Button type="submit" disabled={loading}>
                <Search className="h-4 w-4 mr-2" />
                {loading ? t('dokument.actions.searching') : t('dokument.actions.search')}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Loading */}
      {loading && (
        <Card>
          <CardContent className="flex items-center justify-center py-8 gap-2">
            <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
            <span>{t('dokument.loading.documents')}</span>
          </CardContent>
        </Card>
      )}

      {/* Error */}
      {error && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="pt-6 text-red-700 text-sm">
            {error}
          </CardContent>
        </Card>
      )}

      {/* Results */}
      {data && !loading && (
        <>
          <p className="text-sm text-gray-600">
            <strong>{data.hits.toLocaleString()}</strong>{' '}
            — {t('eurlex.results.pageLabel', { page: data.page })} / {data.total_pages}
          </p>

          {data.dokument.length === 0 ? (
            <Card>
              <CardContent className="py-8 text-center text-gray-500 text-sm">
                {t('dokument.results.noResults')}
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {data.dokument.map((doc) => (
                <DokumentCard key={doc.id || doc.dok_id} doc={doc} />
              ))}
            </div>
          )}

          {/* Pagination */}
          {data.total_pages > 1 && (
            <div className="flex justify-center items-center gap-2 py-4">
              <Button
                variant="outline"
                size="sm"
                disabled={page === 1}
                onClick={() => fetchDocs(page - 1)}
              >
                <ChevronLeft className="h-4 w-4" />
                {t('eurlex.results.previous')}
              </Button>
              <span className="text-sm text-gray-600 px-2">
                {page} / {data.total_pages}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= data.total_pages}
                onClick={() => fetchDocs(page + 1)}
              >
                {t('eurlex.results.next')}
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

// ─── Main component ────────────────────────────────────────────────────────────

const DokumentDashboard = () => {
  const [types, setTypes] = useState<DocType[]>([]);
  const [selectedType, setSelectedType] = useState<DocType | null>(null);
  const [loadingTypes, setLoadingTypes] = useState(true);
  const { t } = useTranslation();

  useEffect(() => {
    const base = getApiBaseUrl();
    fetch(`${base}/chat/api/dokument/types`, { credentials: 'include' })
      .then((r) => r.json())
      .then(setTypes)
      .catch(() => {})
      .finally(() => setLoadingTypes(false));
  }, []);

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <BookOpen className="h-6 w-6" />
          {t('dokument.title')}
        </h2>
        <p className="text-gray-600 mt-1">{t('dokument.description')}</p>
      </div>

      {loadingTypes ? (
        <Card>
          <CardContent className="flex items-center justify-center py-10 gap-2">
            <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
            <span>{t('dokument.loading.types')}</span>
          </CardContent>
        </Card>
      ) : selectedType ? (
        <TypeView docType={selectedType} onBack={() => setSelectedType(null)} />
      ) : (
        <OverviewGrid types={types} onSelect={setSelectedType} />
      )}
    </div>
  );
};

export default DokumentDashboard;
